import type { MouseEvent, ReactNode, Ref } from 'react';
import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import type {
  BreadcrumbLink,
  BreadcrumbLinkClickPayload,
} from '@noths/polaris-design-system-candidate-components';
import { Breadcrumbs } from '@noths/polaris-design-system-candidate-components';

import { OfferLabel } from 'src/components/atoms/OfferLabel/OfferLabel';
import { ProductBadge } from 'src/components/atoms/ProductBadge/ProductBadge';
import type { ProductSummaryViewModel } from 'src/components/layouts/hooks/useProductSummaryViewModel';
import type { ProductFeaturesProps } from 'src/components/molecules/ProductFeatures/components/ProductFeatures';
import { ProductFeatures } from 'src/components/molecules/ProductFeatures/components/ProductFeatures';
import { ProductPrice } from 'src/components/molecules/ProductPrice/ProductPrice';
import { ProductRatingWithTracking } from 'src/components/molecules/ProductRating/ProductRatingWithTracking';
import * as styles from './PDPLayoutView.styles';

export interface PDPLayoutViewProps {
  /** empty = breadcrumbs hidden */
  breadcrumbs: BreadcrumbLink[];
  /** Ref to the CTA grid cell, for drawers rendered outside the view. Only attaches when ctaTrigger is provided. */
  ctaColumnRef?: Ref<HTMLDivElement>;
  /** CTA trigger area (delivery summary, options trigger, basket CTA). Not an inline options section. */
  ctaTrigger?: ReactNode;
  linkCarousel?: ReactNode;
  /** Wider column, spans rows alongside the narrow-column content on desktop */
  media?: ReactNode;
  membershipBanner?: ReactNode;
  /** Called when a breadcrumb link is clicked; not called when breadcrumbs is empty */
  onBreadcrumbLinkClick?: (
    e: MouseEvent<HTMLAnchorElement>,
    payload: BreadcrumbLinkClickPayload,
  ) => void;
  /** Announces state changes to screen readers */
  pageLiveRegionText: string;
  /** Full-width section with tinted background */
  partnerMaxiModule?: ReactNode;
  /** Partner mini module, sits in the narrow column below the CTA */
  partnerMiniModule?: ReactNode;
  partnerRecommendedProducts?: ReactNode;
  /** Taxonomy carousel for the product's deepest breadcrumb node */
  plpPreview?: ReactNode;
  /** empty features = product features section hidden */
  productFeatures?: ProductFeaturesProps;
  productInformation?: ReactNode;
  /** Everything the layout renders about the product itself */
  productSummary: ProductSummaryViewModel;
  publicAttributes?: ReactNode;
  recommendedProducts?: ReactNode;
  /** Slot content owns the reviews fragment id and its internal layout */
  reviews?: ReactNode;
  /** Rendered last; caller provides sticky wrapper and trigger wiring */
  stickyCta?: ReactNode;
  trustBanner?: ReactNode;
}

export const PDPLayoutView = ({
  breadcrumbs,
  ctaColumnRef,
  ctaTrigger,
  linkCarousel,
  media,
  membershipBanner,
  onBreadcrumbLinkClick,
  pageLiveRegionText,
  partnerMaxiModule,
  partnerMiniModule,
  partnerRecommendedProducts,
  plpPreview,
  productFeatures = { features: [] },
  productInformation,
  productSummary,
  publicAttributes,
  recommendedProducts,
  reviews,
  stickyCta,
  trustBanner,
}: PDPLayoutViewProps) => {
  const showRating = Boolean(productSummary.productReviews.rating);

  return (
    <>
      <div css={visuallyHidden} role="status">
        {pageLiveRegionText}
      </div>
      <div css={[styles.maxWidthContainer, styles.grid]}>
        {breadcrumbs.length > 0 && (
          <div css={styles.breadcrumbsArea}>
            <Breadcrumbs links={breadcrumbs} onLinkClick={onBreadcrumbLinkClick} />
          </div>
        )}
        <div css={styles.imagesArea}>{media}</div>
        <div css={styles.titleArea}>
          <h1 css={[styles.productTitle]}>{productSummary.title}</h1>
          <div
            css={styles.descriptionArea}
            dangerouslySetInnerHTML={{ __html: productSummary.description }}
          />
          <section css={styles.priceRow}>
            <ProductPrice
              preSalePrices={productSummary.price.preSalePrices}
              price={productSummary.price.price}
              priceIsMinimum={productSummary.price.priceIsMinimum}
              promoteAsSale={productSummary.price.promoteAsSale}
              salePercentage={productSummary.price.salePercentage}
            />
            {(productSummary.badge || showRating) && (
              <div css={styles.ratingPill}>
                <ProductRatingWithTracking
                  averageRating={productSummary.productReviews.rating ?? undefined}
                  numberOfReviews={productSummary.productReviews.count}
                  reviewsLinkHref={productSummary.productReviews.href}
                />
                {productSummary.badge && <ProductBadge badge={productSummary.badge} />}
              </div>
            )}
          </section>
          {productSummary.offerLabel && (
            <div css={styles.offerLabel}>
              <OfferLabel {...productSummary.offerLabel} />
            </div>
          )}
          {ctaTrigger && (
            <div css={styles.ctaTriggerStack} ref={ctaColumnRef}>
              {ctaTrigger}
            </div>
          )}
          {partnerMiniModule}
          {productFeatures.features.length > 0 && (
            <ProductFeatures
              features={productFeatures.features}
              onVisible={productFeatures.onVisible}
            />
          )}
        </div>
      </div>
      <div css={[styles.maxWidthContainer, styles.column]}>
        {reviews}
        <div css={styles.productDetailsWrapper}>
          {productInformation && (
            <div css={styles.productInformationArea}>{productInformation}</div>
          )}
          {publicAttributes && <div css={styles.publicAttributesArea}>{publicAttributes}</div>}
        </div>
        {plpPreview}
        {partnerMaxiModule}
        {partnerRecommendedProducts}
        {recommendedProducts}
        {trustBanner}
        {membershipBanner}
        {linkCarousel}
        {stickyCta}
      </div>
    </>
  );
};
